import Phaser from 'phaser';
import { canvasFor } from './canvasBox';
import { bleedEdgeRect, boundsCover, displayBounds } from './bleedCover';
import { stage } from './stage';

export { canvasFor };
export { bleedEdgeRect, boundsCover, displayBounds };

const BAND = 12;

function edgeKey(scene: Phaser.Scene, sourceKey: string, edge: 'top' | 'bottom' | 'left' | 'right'): string | null {
  const id = `${sourceKey}__bleed_${edge}`;
  if (scene.textures.exists(id)) return id;
  const texture = scene.textures.get(sourceKey);
  if (!texture || texture.key === '__MISSING') return null;
  const source = texture.getSourceImage() as CanvasImageSource & { width?: number; height?: number };
  const frame = texture.source[0];
  const sw = source.width || frame?.width || 0;
  const sh = source.height || frame?.height || 0;
  if (sw < BAND || sh < BAND) return null;
  const horizontal = edge === 'top' || edge === 'bottom';
  const canvasTex = scene.textures.createCanvas(id, horizontal ? sw : BAND, horizontal ? BAND : sh);
  if (!canvasTex) return null;
  const ctx = canvasTex.getContext();
  const sx = edge === 'right' ? sw - BAND : 0;
  const sy = edge === 'bottom' ? sh - BAND : 0;
  const cropW = horizontal ? sw : BAND;
  const cropH = horizontal ? BAND : sh;
  try {
    ctx.drawImage(source, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
  } catch {
    scene.textures.remove(id);
    return null;
  }
  canvasTex.refresh();
  return id;
}

/** Center the layout and continue its painting into the extra pixels. */
export function fillBleed(scene: Phaser.Scene): void {
  const gameW = scene.scale.width;
  const gameH = scene.scale.height;
  const bleedX = Math.max(0, (gameW - stage.w) / 2);
  const bleedY = Math.max(0, (gameH - stage.h) / 2);
  scene.cameras.main.setScroll(-bleedX, -bleedY);
  if (bleedX < 1 && bleedY < 1) return;

  const bg = scene.children.list.find(
    (child): child is Phaser.GameObjects.Image =>
      child instanceof Phaser.GameObjects.Image && child.displayWidth >= stage.w - 4 && child.displayHeight >= stage.h - 4,
  );
  if (!bg) return;
  const covered = scene.children.list
    .filter((child): child is Phaser.GameObjects.Image => child instanceof Phaser.GameObjects.Image)
    .map((child) => displayBounds(child));
  const key = bg.texture.key;
  const overlap = 2;
  const needs = (edge: 'top' | 'bottom' | 'left' | 'right') => {
    const rect = bleedEdgeRect(edge, stage.w, stage.h, bleedX, bleedY);
    return rect != null && !boundsCover(covered, rect);
  };
  if (bleedY > 0) {
    const topKey = needs('top') ? edgeKey(scene, key, 'top') : null;
    const bottomKey = needs('bottom') ? edgeKey(scene, key, 'bottom') : null;
    if (topKey) {
      scene.add
        .image(stage.w / 2, -(bleedY - overlap) / 2, topKey)
        .setDisplaySize(stage.w + bleedX * 2, bleedY + overlap)
        .setDepth(0);
    }
    if (bottomKey) {
      scene.add
        .image(stage.w / 2, stage.h + (bleedY - overlap) / 2, bottomKey)
        .setDisplaySize(stage.w + bleedX * 2, bleedY + overlap)
        .setDepth(0);
    }
  }
  if (bleedX > 0) {
    const leftKey = needs('left') ? edgeKey(scene, key, 'left') : null;
    const rightKey = needs('right') ? edgeKey(scene, key, 'right') : null;
    if (leftKey) {
      scene.add
        .image(-(bleedX - overlap) / 2, stage.h / 2, leftKey)
        .setDisplaySize(bleedX + overlap, stage.h)
        .setDepth(0);
    }
    if (rightKey) {
      scene.add
        .image(stage.w + (bleedX - overlap) / 2, stage.h / 2, rightKey)
        .setDisplaySize(bleedX + overlap, stage.h)
        .setDepth(0);
    }
  }
}

/** Installed on every scene so the painting reaches the edges of the phone. */
export class BleedPlugin extends Phaser.Plugins.ScenePlugin {
  override boot(): void {
    const events = this.systems?.events;
    if (!events) return;
    const fill = () => {
      if (this.scene) fillBleed(this.scene);
    };
    events.on(Phaser.Scenes.Events.CREATE, fill);
    events.once(Phaser.Scenes.Events.DESTROY, () => {
      events.off(Phaser.Scenes.Events.CREATE, fill);
    });
  }
}
